import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly } from '@/lib/utils';
import { Button } from './ui/primitives';
import { useToast } from './ui/toast';

interface SignatureNotice {
  addedBy: string | null;
  addedAt: string;
}

export function SignatureNoticeBanner() {
  const { user, canWrite } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const staff = Boolean(user?.tenantId) && !user?.platformRole && canWrite;

  const notice = useQuery({
    queryKey: ['signature-notice', user?.id],
    queryFn: () => get<{ notice: SignatureNotice | null }>('/org/users/me/signature-notice'),
    enabled: staff,
    staleTime: Infinity,
    retry: false,
  });

  const settle = () => void queryClient.invalidateQueries({ queryKey: ['signature-notice'] });

  const keep = useMutation({
    mutationFn: () => post('/org/users/me/signature-notice/keep'),
    onSuccess: settle,
    onError: (caught) => toast.error('Could not save your choice', describeError(caught)),
  });

  const remove = useMutation({
    mutationFn: () => del(`/org/users/${user?.id}/signature`),
    onSuccess: () => {
      toast.success('Your signature was removed');
      settle();
    },
    onError: (caught) => toast.error('Could not remove the signature', describeError(caught)),
  });

  const current = notice.data?.notice;
  if (!staff || !current) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100 lg:px-6">
      <span className="flex-1">
        Your signature was added by {current.addedBy ?? 'a colleague'} on {dateOnly(current.addedAt)}. It prints on documents you sign.
      </span>
      <Button size="sm" variant="secondary" loading={keep.isPending} onClick={() => keep.mutate()}>
        Keep it
      </Button>
      <Button size="sm" variant="ghost" loading={remove.isPending} onClick={() => remove.mutate()}>
        Remove it
      </Button>
    </div>
  );
}
